// src/hooks/coinglass/useETFInflowChart.ts

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { etfInflowChartQueryOptions } from "@/services/queries/coinglass/coinglassQueries";
import { useTokenPriceData } from "./useTokenPriceData";
import type { ETFInflowChartWithPrice } from "@/types/etf";

/**
 * Custom hook for fetching ETF inflow chart data combined with price data
 *
 * @param ticker - Token ticker symbol (lowercase: "btc", "eth", "sol")
 * @returns Query result with combined chart data (inflows, outflows, and price)
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useETFInflowChart("btc");
 * // data: Array<{ date, inflow, outflow, tokenPrice }>
 * ```
 */
export function useETFInflowChart(ticker: string) {
  // Fetch chart data (inflows/outflows)
  const chartQuery = useQuery(etfInflowChartQueryOptions({ ticker }));

  // Fetch price data separately
  const { data: priceData, isLoading: priceLoading } = useTokenPriceData(ticker.toUpperCase());

  // Combine chart data with price data
  const combinedData: ETFInflowChartWithPrice[] = useMemo(() => {
    if (!chartQuery.data?.data || !priceData) return [];

    const chartDataPoints = chartQuery.data.data;

    // Create a map of date to price for quick lookup
    const priceMap = new Map<string, number>();
    priceData.forEach((point) => {
      // Convert timestamp to yyyy-MM-dd format
      const date = new Date(point.timestamp).toISOString().split("T")[0];
      if (date && point.price !== null) {
        priceMap.set(date, point.price);
      }
    });

    // Combine chart data with prices
    return chartDataPoints
      .map((point) => {
        const price = priceMap.get(point.date);
        // Only include points where we have price data
        if (price === undefined) return null;

        return {
          date: point.date,
          inflow: point.inflow,
          outflow: point.outflow,
          tokenPrice: price,
        };
      })
      .filter((point): point is ETFInflowChartWithPrice => point !== null);
  }, [chartQuery.data, priceData]);

  return {
    data: combinedData,
    isLoading: chartQuery.isLoading || priceLoading,
    isError: chartQuery.isError,
    error: chartQuery.error,
    refetch: chartQuery.refetch,
  };
}
